import { useCallback, useState } from "react";
import { useAtomValue, useStore } from "jotai";
import { Button, ErrorState, Spinner, Title } from "@common/index";
import { ComponentColor } from "@common/types";
import { ROUTES } from "@routing/routes";
import { useOrganizationApi } from "@/hooks/apiHooks/useOrganizationApi";
import { useUserApi } from "@/hooks/apiHooks/useUserApi";
import { useMountRead } from "@/hooks/useMountRead";
import { organizationsAtom } from "@/store/organizationAtoms";
import { currentUserAtom } from "@/store/userAtoms";
import type { ReturnedPaymentOrder } from "@models/user";
import { AddPaymentMethodPanel } from "../AddPaymentMethodPanel";
import { isOrganizationAdmin } from "../_helpers/isOrganizationAdmin";
import {
    returnedPaymentAdminNext,
    returnedPaymentLead,
    returnedPaymentMemberNext,
    returnedPaymentSaved,
} from "../_helpers/returnedPaymentCopy";

export interface PaymentReturnedProps {
    userId: string;
    orders: ReturnedPaymentOrder[];
    hasCardSinceReturn: boolean;
}

// The suspended wall's variant when the suspension is a RETURNED bank payment
// (#445 / audit C-11, DecisionLog §3.13 Q1–Q8). Ops collects the money outside
// the app and an admin lifts the hold, so nothing here lifts it: the page asks
// the organization's Admin for a new card, and says thanks once one is saved.
//
// "Saved" is `hasCardSinceReturn` from users/me and nothing else, on a revisit
// and right after a save alike. After a save the user is refreshed (by the card
// panel on a 200, by AskForCard otherwise), and that refresh is what moves this
// page to the thanks.
export const PaymentReturned = ({
    userId,
    orders,
    hasCardSinceReturn,
}: PaymentReturnedProps) => {
    // Set when a save stored a card that the panel's own refresh did not report:
    // a 402 after the card was stored, or a 200 whose refresh failed. The panel
    // then holds something the customer must still see (its error), so it stays
    // on screen beside the thanks rather than being swapped out with it
    // (DecisionLog §3.13, #445 "Lead rulings for the UI (2026-10-23)").
    const [keepPanel, setKeepPanel] = useState(false);

    return (
        <>
            <Title>Payment Returned</Title>
            {hasCardSinceReturn && !keepPanel ? (
                // Said to every member (DecisionLog §3.13, #445 "Lead rulings for
                // the UI (2026-10-23)"): once a card is saved, nobody is asked for
                // one. No organization read is needed for this.
                <p className="text-base mt-6">
                    {returnedPaymentLead(orders)} {returnedPaymentSaved(orders)}
                </p>
            ) : (
                <AskForCard
                    userId={userId}
                    orders={orders}
                    cardSaved={hasCardSinceReturn}
                    onUnreportedSave={() => setKeepPanel(true)}
                />
            )}
            <div className="mt-6">
                <Button
                    to={ROUTES.SUPPORT}
                    color={ComponentColor.Primary}
                    outline
                    className="normal-case"
                >
                    Contact Support
                </Button>
            </div>
        </>
    );
};

interface AskForCardProps {
    userId: string;
    orders: ReturnedPaymentOrder[];
    /** hasCardSinceReturn, true here only while the panel is kept after a save. */
    cardSaved: boolean;
    /** A save stored a card, and the panel's own user refresh did not land. */
    onUnreportedSave: () => void;
}

// Before a card is saved, what follows the first sentence depends on the
// user's organization role, which only organizations/me reports. Until that
// read answers, only the first sentence is said; a failed read says so and
// offers Retry rather than guessing a role.
const AskForCard = ({
    userId,
    orders,
    cardSaved,
    onUnreportedSave,
}: AskForCardProps) => {
    const { getMeOrganizations } = useOrganizationApi();
    const { getMeUser } = useUserApi();
    const store = useStore();
    const user = useAtomValue(currentUserAtom);
    const organizations = useAtomValue(organizationsAtom);
    // `silent`: a failure renders the inline ErrorState below with its own
    // Retry, so useApi's toast would announce the same failure twice
    // (AddPaymentMethodPanel reads it the same way). Wrapped so the `isCurrent`
    // useMountRead hands a read never reaches getMeOrganizations as its options.
    const readOrganizations = useCallback(
        () => getMeOrganizations({ silent: true }),
        [getMeOrganizations],
    );
    const { readStatus, reload } = useMountRead(readOrganizations);

    // A user belongs to exactly one organization; the endpoint returns an array.
    const organization = organizations[0];
    const lead = returnedPaymentLead(orders);

    // The panel calls this once per attempt that left a new card on file: after a
    // 200, once its own getMeUser refresh has run, and after a 402 that stored the
    // card, with no refresh at all. The panel captured this callback, and so
    // `user`, when the save began. A refresh that landed has replaced that user in
    // the store, and reading users/me again would repeat it. Otherwise (the 402,
    // or a 200 whose refresh failed) the page refreshes the user itself, through
    // the same getMeUser, and keeps the panel so its message stays on screen.
    const refreshAfterSave = () => {
        if (store.get(currentUserAtom) !== user) return;

        onUnreportedSave();
        // A failure leaves the panel's own state as the record of the save; the
        // thanks then appears on the next visit.
        getMeUser().catch(() => {});
    };

    if (readStatus === "loading" && !organization) {
        return (
            <>
                <p className="text-base mt-6">{lead}</p>
                <div className="flex justify-center py-10">
                    <Spinner size="lg" />
                </div>
            </>
        );
    }

    if (readStatus === "failed" && !organization) {
        return (
            <>
                <p className="text-base mt-6">{lead}</p>
                <ErrorState
                    message="Couldn't load your organization."
                    onRetry={() => void reload()}
                />
            </>
        );
    }

    if (!organization || !isOrganizationAdmin(organization, userId)) {
        return (
            <p className="text-base mt-6">
                {lead} {returnedPaymentMemberNext(orders)}
            </p>
        );
    }

    return (
        <>
            <p className="text-base mt-6">
                {lead}{" "}
                {cardSaved
                    ? returnedPaymentSaved(orders)
                    : returnedPaymentAdminNext(orders)}
            </p>
            <div className="mt-8">
                <AddPaymentMethodPanel
                    variant="update"
                    currentCard={organization.paymentMethod ?? null}
                    organizationId={organization.id}
                    onSaved={refreshAfterSave}
                />
            </div>
        </>
    );
};
